import React, { useEffect } from 'react';
import BackButton from '../Components/Simple/BackButton';
import Input from '../Components/Simple/Input';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import ApiError from '../Components/Combined/ApiError';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { SNF as modelType } from '../Constants/modelTypes';
import { EntryTable, EntryTableColumn } from '../Components/Combined/EntryTable';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import { formatDateStringIfMatches, getDefaultDateString } from '../Utils/stringUtils';

import ErrorAlert from '../Components/Simple/ErrorAlert';
import Checkbox from '../Components/Simple/Checkbox';
import useFiscalYear from '../Hooks/useFiscalYear';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { mapToSetters } from '../Utils/mapping';
import { Button, ErrorMessage } from '../Components/Simple';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { ProviderMapper } from '../Components/Combined';
import { enableAsyncPricingApi } from '../Utils/features';
import usePricerApi from '../Hooks/usePricerApi';

export default function SNF() {

    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedNPI } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [zipCode, setZipCode] = useSessionState('', 'zipCode');
    const [hasAIDSDiagnosis, setHasAIDSDiagnosis] = useSessionState(false, 'hasAIDSDiagnosis');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [previousDays, setPreviousDays] = useSessionState('', 'previousDays');
    const [billedAmountTotal, setBilledAmountTotal] = useSessionState('', 'billedAmountTotal');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');

    const [fiscalYear, setFiscalYearInputDate] = useFiscalYear(null);

    const navigate = useNavigate();

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            revenueCode: '',
            procedureCode: '',
            // serviceDate: '',
            units: 0
        };
    };

    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);
    const warmupPricer = useAsyncPricerApi(modelType, true);

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        claimId,
        medicareId,
        zipCode,
        hasAIDSDiagnosis,
        previousDays,
        fromDate,
        toDate,
        lines: linesCollection.values,
        npi,
        providerName
    });

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        pricer.reset();
        fileImportOnChange(e);
    };

    const handleSubmit = async () => {
        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(fiscalYear, formatRequest());
        }
    };

    const handleFileSave = () => {
        pricer.reset();
        fileExportExecute();
    };

    const formatRequest = () => {
        return {
            claimId: claimId || null,
            medicareId: medicareId || null,
            zipCode: zipCode || null,
            fromDate: formatDateStringIfMatches(fromDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toDate: formatDateStringIfMatches(toDate || null, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            icdDiag1: hasAIDSDiagnosis ? 'B20' : '',
            previousDays: isNaN(previousDays) || isNaN(parseFloat(previousDays)) ? "0" : previousDays,
            serviceLineItems: linesCollection.values.filter(l => l.procedureCode).map(l => {
                return {
                    lineId: !l.lineId && l.lineId !== 0 ? null : l.lineId.toString(),
                    revCode: l.revenueCode || null,
                    HCPCS: l.procedureCode || null,
                    unitsDays: !l.units && l.units !== 0 ? null : l.units
                }
            })
        };
    }

    const handleReset = () => {
        setClaimId('');
        setMedicareId('');
        setZipCode('');
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        setHasAIDSDiagnosis(false);
        setProviderName('');
        setNpi('');
        linesCollection.setValues(getDefaultLines());
        pricer.reset();
        fileExportReset();
        fileImportReset();
    };

    useEffect(() => {
        if (fileImportSuccess) {
            const setters = {
                claimId: setClaimId,
                medicareId: setMedicareId,
                zipCode: setZipCode,
                hasAIDSDiagnosis: setHasAIDSDiagnosis,
                previousDays: setPreviousDays,
                fromDate: setFromDate,
                toDate: setToDate,
                lines: linesCollection.setValues,
                providerName: setProviderName,
                npi: setNpi
            };
            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        setFiscalYearInputDate(fromDate);
    }, [fromDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    fiscalYear,
                    result: pricer.result,
                    billedAmountTotal
                }
            });
        }
    }, [pricer.success]);

    return (
        <div className="py-2">
            <div className="row my-2 justify-content-between sticky top g-0 border-bottom align-items-center">
                <div className="col-auto">
                    <h3>SNF Pricer</h3>
                    <div className="fs-6">
                        Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                    </div>
                </div>
                <div className="col-auto">
                    <BackButton />
                </div>
            </div>
            {(pricer.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            <form onSubmit={handleSubmit}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Zip Code" maxLength={5} labelClassName="fw-bold" id="zip-code" onChange={setZipCode} value={zipCode} />
                    </div>
                    <div className="col-md-3 mt-4">
                        <Checkbox label="Has AIDS diag (diag = B20)" labelClassName="fw-bold" id="has-aids-diag" onChange={setHasAIDSDiagnosis} value={hasAIDSDiagnosis} />
                    </div>
                </div>
                <div className="fst-italic">SNF Claims can be entered incrementally. To do this, enter From Date and To Date for the new days, and set previous days equal to the sum of all days on prior bills for the same stay.</div>
                <div className="fst-italic">If a claim spans the fiscal year (crosses from September to October), separate bills must be entered for before and after.</div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="From Date" labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder="MM/DD/YYYY" />
                    </div>
                    <div className="col-md-3">
                        <Input label="To Date" labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder="MM/DD/YYYY" />
                    </div>
                    <div className="col-md-3">
                        <Input label="Previous Days" labelClassName="fw-bold" id="previous-days" onChange={setPreviousDays} value={previousDays} />
                    </div>
                    <div className="col-md-3">
                        <Input label="Billed Amount - Total" labelClassName="fw-bold" id="billed-amount-total" onChange={setBilledAmountTotal} value={billedAmountTotal} placeholder="0000.00" />
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        <div className="fst-italic">Lines must be entered in chronological order.</div>
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            upDownNavigation={true}
                            max={20}
                            maxWarningMessage="Too many claim lines entered.  Enter remaining lines on a separate claim.">
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-25" />
                            <EntryTableColumn label="Revenue Code"
                                name="revenueCode"
                                type="text" />
                            <EntryTableColumn label="HIPPS Code"
                                name="procedureCode"
                                type="text" />
                            <EntryTableColumn label="Units (Days)"
                                name="units"
                                type="number"
                                className="mw-75" />
                        </EntryTable>
                    </div>
                </div>

                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    onChange={handleFileImportOnChange}
                                    loading={fileImporting}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={pricer.error || fileImportError || fileExportError} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="reset-button"
                                    onClick={handleReset}
                                    disabled={pricer.loading}
                                    text="Reset" />
                            </div>
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    disabled={pricer.loading || fileImporting || fileExporting}
                                    loading={pricer.loading}
                                    onClick={handleSubmit}
                                    text="Submit" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
